import React, { useCallback, useEffect } from "react";

import { faAlignJustify, faGripLines } from "@fortawesome/pro-solid-svg-icons";
import { faLongArrowUp } from "@fortawesome/pro-solid-svg-icons/faLongArrowUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { isToday } from "date-fns/esm";
import { useIntl } from "react-intl";

import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { Button } from "@mapmycustomers/ui";

import Search from "@app/scene/dashboard/components/Search";
import TimelineEventType from "@app/scene/dashboard/enum/TimelineEventType";
import TimelineViewFilter from "@app/scene/dashboard/enum/TimelineViewFilter";
import useAnalytics from "@app/util/contexts/useAnalytics";

import CustomTypeFilter from "./components/CustomTypeFilter";
import ScrollTo from "./components/ScrollTo";
import TypeSelector from "./components/TypeSelector";
import styles from "./FilterBar.module.scss";

interface Props {
  className?: string;
  eventTypes: TimelineEventType[];
  eventTypesPopoverOpen?: boolean;
  onEventTypesChange?: (eventTypes: TimelineEventType[]) => void;
  onEventTypesPopoverOpenChange?: (open: boolean) => void;
  onQueryChange: (query: string) => void;
  onSetEventsExpanded: (expand: boolean) => void;
  onStartDateChange?: (date: Date) => void;
  onViewFilterChange?: (typeFilter: TimelineViewFilter | undefined) => void;
  query?: string;
  startDate?: Date;
  viewFilter?: TimelineViewFilter;
}

const FilterBar: React.FC<Props> = ({
  className,
  eventTypes,
  eventTypesPopoverOpen,
  onEventTypesChange,
  onEventTypesPopoverOpenChange,
  onQueryChange,
  onSetEventsExpanded,
  onStartDateChange,
  onViewFilterChange,
  query,
  startDate,
  viewFilter,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const [innerQuery, setInnerQuery, resetInnerQuery] = useStateWithDebouncedListener(
    [onQueryChange, 500],
    query ?? ""
  );
  useEffect(() => {
    resetInnerQuery(query ?? "");
  }, [resetInnerQuery, query]);

  const showScrollToTop = startDate && !isToday(startDate);
  const handleBackToTopClick = useCallback(() => {
    analytics.clicked(["Scroll to", "Back to top"]);
    onStartDateChange?.(new Date());
  }, [onStartDateChange, analytics]);

  const handleScrollToChange = useCallback(
    (date: Date) => {
      analytics.clicked(["Scroll to", "Select date"]);
      onStartDateChange?.(date);
    },
    [analytics, onStartDateChange]
  );

  const handleCollapseAll = useCallback(() => onSetEventsExpanded(false), [onSetEventsExpanded]);
  const handleExpandAll = useCallback(() => onSetEventsExpanded(true), [onSetEventsExpanded]);

  return (
    <div className={cn(styles.container, className)}>
      <TypeSelector onChange={onViewFilterChange} value={viewFilter} />
      <div className={styles.rightPart}>
        <ScrollTo onChange={handleScrollToChange} value={startDate} />
        <Tooltip
          title={intl.formatMessage({
            id: "dashboard.timeline.header.collapse",
            defaultMessage: "Collapse All",
            description: "Collapse All button",
          })}
        >
          <Button iconOnly onClick={handleCollapseAll}>
            <FontAwesomeIcon icon={faGripLines} />
          </Button>
        </Tooltip>
        <Tooltip
          title={intl.formatMessage({
            id: "dashboard.timeline.header.expand",
            defaultMessage: "Expand All",
            description: "Expand All button",
          })}
        >
          <Button iconOnly onClick={handleExpandAll}>
            <FontAwesomeIcon icon={faAlignJustify} />
          </Button>
        </Tooltip>
        <Search onChange={setInnerQuery} value={innerQuery} />
        <CustomTypeFilter
          eventTypes={eventTypes}
          onEventTypesChange={onEventTypesChange}
          onOpenChange={onEventTypesPopoverOpenChange}
          open={eventTypesPopoverOpen}
        />
      </div>
      {showScrollToTop && (
        <div className={styles.backToTop} onClick={handleBackToTopClick}>
          {intl.formatMessage(
            {
              id: "dashboard.timeline.header.backToTop",
              defaultMessage: "Back to top {icon}",
              description: "Back to top button title, which appears on a Timeline view",
            },
            { icon: <FontAwesomeIcon icon={faLongArrowUp} /> }
          )}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
